import { PropsWithChildren, ReactElement, cloneElement, createContext, useContext, useState } from 'react';
import { Drawer } from '@mui/material';
import StyledDialog from '../components/atoms/StyledDialog';
import type { StyledDialogSize } from '../components/atoms/StyledDialog';
import { useBreakpoints } from '../hooks/useBreakpoints';

const styles = {
  drawer: {
    '& .MuiDrawer-paper': {
      p: 1,
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
      maxHeight: '80vh',
    },
  },
};

type ModalState<T> = {
  isOpen: boolean;
  component?: ReactElement<{ closeModal?: () => void }>;
  item?: T;
  options?: ModalOptions;
};

export type ModalOptions = {
  dialogSize?: StyledDialogSize;
};

export type OpenModal<T> = (
  content: ReactElement<{ closeModal?: () => void }>,
  item?: T,
  options?: ModalOptions
) => void;

type ModalContextType<T> = {
  openModal: OpenModal<T>;
  closeModal: () => void;
  item?: T;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ModalContext = createContext<ModalContextType<any> | null>(null);

const initialState = {
  isOpen: false,
  component: undefined,
  item: undefined,
};
export const ModalProvider = <T,>({ children }: PropsWithChildren) => {
  const [state, setState] = useState<ModalState<T>>({ isOpen: false });
  const { isMobile } = useBreakpoints();

  const openModal: OpenModal<T> = (component, item, options) => {
    setState({ component, isOpen: true, item, options });
  };

  const closeModal = () => {
    setState(initialState);
  };

  const handleClose = (event: object, reason: string) => {
    if (reason !== 'backdropClick') {
      closeModal?.();
    }
  };

  const modalContent = state.component && cloneElement(state.component, { closeModal });

  return (
    <ModalContext.Provider value={{ openModal, closeModal, item: state.item }}>
      {children}
      {isMobile ? (
        <Drawer anchor='bottom' open={state.isOpen} onClose={handleClose} sx={styles.drawer}>
          {modalContent}
        </Drawer>
      ) : (
        <StyledDialog
          open={state.isOpen}
          onClose={handleClose}
          transitionDuration={{ exit: 0 }}
          dialogSize={state.options?.dialogSize}
        >
          {modalContent}
        </StyledDialog>
      )}
    </ModalContext.Provider>
  );
};

export const useModal = <T,>() => {
  const context = useContext(ModalContext) as ModalContextType<T> | null;

  if (context === null) {
    throw new Error('useModal must be used within a Modal Provider');
  }
  return context;
};
